/**
 * Dashboard page component.
 * Modern bento grid layout with premium stat cards and real-time activity.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  SmartToy as AgentIcon,
  Chat as SessionIcon,
  Bolt as ExecutionIcon,
  AttachMoney as CostIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon,
  Tune as TuneIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';


import { RealTimeMetrics } from '../components/dashboard/RealTimeMetrics';
import { ROUTES } from '../utils/routes';
import { dashboardService } from '../services/dashboardService';
import type { UsageTrendDto } from '../types/dashboard';

import {
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart,
} from 'recharts';

/**
 * Stat Card Component - Premium bento widget
 */
const StatCard = ({
  icon,
  label,
  value,
  gradient,
  loading,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  gradient: string;
  loading: boolean;
}) => (
  <Box
    sx={{
      p: 4,
      borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
      border: '1px solid var(--glass-border)',
      backgroundImage: 'var(--gradient-surface)',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      transition: 'border-color 200ms ease, box-shadow 200ms ease',
      '&:hover': {
        borderColor: 'rgba(255, 255, 255, 0.12)',
        boxShadow: 'var(--glow-primary)',
      },
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
        background: gradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        '& svg': { fontSize: '1.25rem' },
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="overline" sx={{ color: 'var(--mui-palette-text-secondary)', fontWeight: 500, letterSpacing: '0.05em' }}>
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="text" width={80} height={36} sx={{ bgcolor: 'var(--glass-bg-subtle)' }} />
      ) : (
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
          {value}
        </Typography>
      )}
    </Box>
  </Box>
);

/**
 * Quick Action Card - Clickable nav card
 */
const QuickActionCard = ({
  icon,
  title,
  description,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}) => (
  <Box
    onClick={onClick}
    sx={{
      p: 4,
      borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
      border: '1px solid var(--glass-border)',
      backgroundImage: 'var(--gradient-surface)',
      cursor: 'pointer',
      transition: 'all 200ms ease',
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      '&:hover': {
        borderColor: 'rgba(255, 255, 255, 0.12)',
        transform: 'translateY(-2px)',
        boxShadow: 'var(--mui-customShadows-md)',
      },
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 'var(--mui-shape-customBorderRadius-md)',
        background: 'var(--gradient-primary-subtle)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '& svg': { fontSize: '1.25rem', color: 'var(--mui-palette-primary-main)' },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {description}
      </Typography>
    </Box>
    <ArrowForwardIcon sx={{ fontSize: '1rem', color: 'var(--mui-palette-text-disabled)' }} />
  </Box>
);

/**
 * Dashboard page with bento grid layout.
 */
export const DashboardPage = () => {
  const navigate = useNavigate();
  const [dateRangeDays] = useState(30);

  const { startDateUtc, endDateUtc } = useMemo(() => {
    const end = new Date();
    const start = new Date(Date.now() - dateRangeDays * 24 * 60 * 60 * 1000);
    const toYmd = (d: Date) => d.toISOString().slice(0, 10);
    return { startDateUtc: toYmd(start), endDateUtc: toYmd(end) };
  }, [dateRangeDays]);

  const { data: mainMetrics, isLoading: mainLoading, error: mainError } = useQuery({
    queryKey: ['dashboard', 'mainMetrics'],
    queryFn: () => dashboardService.getMainMetrics(),
  });

  const { data: usageTrends, isLoading: trendsLoading, error: trendsError } = useQuery({
    queryKey: ['dashboard', 'usageTrends', startDateUtc, endDateUtc],
    queryFn: () => dashboardService.getUsageTrends(startDateUtc, endDateUtc),
  });

  const { data: costBreakdown, isLoading: costLoading, error: costError } = useQuery({
    queryKey: ['dashboard', 'costBreakdown', startDateUtc, endDateUtc],
    queryFn: () => dashboardService.getCostBreakdown(startDateUtc, endDateUtc),
  });

  const { data: agentPerformance, isLoading: perfLoading, error: perfError } = useQuery({
    queryKey: ['dashboard', 'agentPerformance', startDateUtc, endDateUtc],
    queryFn: () => dashboardService.getAgentPerformance(startDateUtc, endDateUtc),
  });

  const formatNumber = (value: number) => new Intl.NumberFormat('en-US').format(value);
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 4 }).format(value);

  const usageTrendChartData = (usageTrends ?? []).map((p: UsageTrendDto) => ({
    date: new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    tokens: p.tokens,
    cost: p.cost,
    sessions: p.sessions,
    executions: p.executions,
  }));

  const pieColors = ['#8B5CF6', '#34D399', '#F87171', '#FBBF24', '#22D3EE', '#94A3B8'];
  const costByModelData = (costBreakdown?.byModel ?? [])
    .filter((x) => x.cost > 0)
    .slice(0, 6)
    .map((x) => ({ name: x.name, value: x.cost }));

  const hasError = mainError || trendsError || costError || perfError;

  return (
    <Box sx={{ animation: 'fade-in 300ms ease-out' }}>
      {/* Page Header */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
          Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Welcome back. Here's your system overview.
        </Typography>
      </Box>

      {hasError && (
        <Alert severity="error" sx={{ mb: 4 }}>
          Failed to load dashboard data. Ensure the backend API is running and you have selected a tenant/project.
        </Alert>
      )}

      {/* Bento Grid - Row 1: Stats */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 3,
          mb: 3,
        }}
      >
        <StatCard
          icon={<AgentIcon />}
          label="Active Agents"
          value={mainMetrics ? formatNumber(mainMetrics.activeAgents) : '-'}
          gradient="var(--gradient-primary)"
          loading={mainLoading}
        />
        <StatCard
          icon={<SessionIcon />}
          label="Active Sessions"
          value={mainMetrics ? formatNumber(mainMetrics.activeSessions) : '-'}
          gradient="linear-gradient(135deg, #059669 0%, #10B981 100%)"
          loading={mainLoading}
        />
        <StatCard
          icon={<ExecutionIcon />}
          label="Executions Today"
          value={mainMetrics ? formatNumber(mainMetrics.totalExecutionsToday) : '-'}
          gradient="linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)"
          loading={mainLoading}
        />
        <StatCard
          icon={<CostIcon />}
          label="Cost Today"
          value={mainMetrics ? formatCurrency(mainMetrics.totalCostToday) : '-'}
          gradient="linear-gradient(135deg, #D97706 0%, #F59E0B 100%)"
          loading={mainLoading}
        />
      </Box>

      {/* Bento Grid - Row 2: Charts + Real-time */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: 3,
          mb: 3,
        }}
      >
        {/* Usage Trend Chart */}
        <Box
          sx={{
            p: 5,
            borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
            border: '1px solid var(--glass-border)',
            backgroundImage: 'var(--gradient-surface)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Usage Trends
          </Typography>
          <Box sx={{ height: 300 }}>
            {trendsLoading ? (
              <Skeleton variant="rounded" height={300} sx={{ borderRadius: 2, bgcolor: 'var(--glass-bg-subtle)' }} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={usageTrendChartData}>
                  <defs>
                    <linearGradient id="tokenGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#34D399" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#34D399" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'rgba(148, 163, 184, 0.6)' }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11, fill: 'rgba(148, 163, 184, 0.6)' }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: 'rgba(148, 163, 184, 0.6)' }} axisLine={false} tickLine={false} />
                  <RechartsTooltip
                    contentStyle={{
                      background: 'rgba(18, 18, 26, 0.9)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderRadius: 12,
                      backdropFilter: 'blur(16px)',
                      fontSize: 12,
                    }}
                  />
                  <Area yAxisId="left" type="monotone" dataKey="tokens" stroke="#8B5CF6" strokeWidth={2} fill="url(#tokenGrad)" dot={false} />
                  <Area yAxisId="right" type="monotone" dataKey="cost" stroke="#34D399" strokeWidth={2} fill="url(#costGrad)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#8B5CF6' }} />
              <Typography variant="caption" color="text.secondary">Tokens</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#34D399' }} />
              <Typography variant="caption" color="text.secondary">Cost</Typography>
            </Box>
          </Box>
        </Box>

        {/* Real-time Metrics */}
        <RealTimeMetrics />
      </Box>

      {/* Bento Grid - Row 3: Pie + Summary */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 3,
          mb: 3,
        }}
      >
        {/* Cost by Model */}
        <Box
          sx={{
            p: 5,
            borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
            border: '1px solid var(--glass-border)',
            backgroundImage: 'var(--gradient-surface)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Cost by Model
          </Typography>
          <Box sx={{ height: 280 }}>
            {costLoading ? (
              <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto', bgcolor: 'var(--glass-bg-subtle)' }} />
            ) : costByModelData.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography variant="body2" color="text.secondary">No cost data available</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={costByModelData} dataKey="value" nameKey="name" outerRadius={90} innerRadius={50} paddingAngle={2}>
                    {costByModelData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} stroke="none" />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(v: any) => formatCurrency(Number(v))}
                    contentStyle={{
                      background: 'rgba(18, 18, 26, 0.9)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderRadius: 12,
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Box>

        {/* Today Summary */}
        <Box
          sx={{
            p: 5,
            borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
            border: '1px solid var(--glass-border)',
            backgroundImage: 'var(--gradient-surface)',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Today Summary
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            {[
              { label: 'Tokens', value: mainMetrics ? formatNumber(mainMetrics.totalTokensToday) : '-' },
              { label: 'API Calls', value: mainMetrics ? formatNumber(mainMetrics.totalApiCallsToday) : '-' },
              { label: 'Storage', value: mainMetrics ? formatNumber(mainMetrics.storageUsedBytes) + ' bytes' : '-' },
              { label: 'Updated', value: mainMetrics ? new Date(mainMetrics.asOfUtc).toLocaleTimeString() : '-' },
            ].map((item) => (
              <Box key={item.label}>
                <Typography variant="overline" sx={{ color: 'var(--mui-palette-text-secondary)', letterSpacing: '0.05em' }}>
                  {item.label}
                </Typography>
                {mainLoading ? (
                  <Skeleton variant="text" width={80} height={28} sx={{ bgcolor: 'var(--glass-bg-subtle)' }} />
                ) : (
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {item.value}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Bento Grid - Row 4: Agent Performance Table */}
      <Box
        sx={{
          p: 5,
          mb: 3,
          borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
          border: '1px solid var(--glass-border)',
          backgroundImage: 'var(--gradient-surface)',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Agent Performance
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell align="right">Executions</TableCell>
                <TableCell align="right">Success Rate</TableCell>
                <TableCell align="right">Avg Latency</TableCell>
                <TableCell align="right">Total Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {perfLoading
                ? [...Array(4)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(5)].map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton variant="text" width={60} sx={{ bgcolor: 'var(--glass-bg-subtle)' }} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : (agentPerformance ?? [])
                    .slice()
                    .sort((a, b) => (b.totalCost ?? 0) - (a.totalCost ?? 0))
                    .slice(0, 8)
                    .map((a) => (
                      <TableRow key={a.agentId}>
                        <TableCell sx={{ fontWeight: 500 }}>{a.agentName}</TableCell>
                        <TableCell align="right">{formatNumber(a.executionCount)}</TableCell>
                        <TableCell align="right">{`${Math.round(a.successRate * 100)}%`}</TableCell>
                        <TableCell align="right">{formatNumber(Math.round(a.averageLatencyMs))}ms</TableCell>
                        <TableCell align="right">{formatCurrency(a.totalCost)}</TableCell>
                      </TableRow>
                    ))}
              {!perfLoading && (agentPerformance ?? []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">No agent performance data available</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Bento Grid - Row 5: Quick Actions */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Quick Actions
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 2,
          }}
        >
          <QuickActionCard icon={<PeopleIcon />} title="Manage Agents" description="Create and configure AI agents" onClick={() => navigate(ROUTES.AGENTS)} />
          <QuickActionCard icon={<TrendingUpIcon />} title="View Sessions" description="Monitor active sessions" onClick={() => navigate(ROUTES.SESSIONS)} />
          <QuickActionCard icon={<SettingsIcon />} title="Configure Tools" description="Manage tools and integrations" onClick={() => navigate(ROUTES.TOOLS)} />
          <QuickActionCard icon={<TuneIcon />} title="Customize Dashboard" description="Arrange widgets and views" onClick={() => navigate(ROUTES.DASHBOARD_CUSTOMIZE)} />
          <QuickActionCard icon={<AssessmentIcon />} title="View Reports" description="Access analytics and reports" onClick={() => navigate(ROUTES.USAGE_ANALYTICS)} />
        </Box>
      </Box>
    </Box>
  );
};
